const SITE = "https://manasneuropsychiatryhospital.in";

const clinicNode = {
  "@type": ["MedicalOrganization", "Hospital"],
  "@id": `${SITE}/#organization`,
  name: "Manas Neuropsychiatry Hospital",
  url: SITE,
  telephone: "+91-94275-60101",
  medicalSpecialty: ["Psychiatry", "Neuropsychiatry", "Addiction Medicine", "Sexology"],
  address: {
    "@type": "PostalAddress",
    streetAddress: "2nd floor, Carlton Square, Near Madhavdeep Complex, Kalanala",
    addressLocality: "Bhavnagar",
    postalCode: "364001",
    addressRegion: "Gujarat",
    addressCountry: "IN",
  },
};

export const therapies = {
  rtms: {
    slug: "rtms",
    eyebrow: "rTMS · FDA-approved · Non-invasive",
    h1a: "No anaesthesia. No admission.",
    h1b: "You walk in and walk out.",
    seoTitle: "rTMS Treatment in Bhavnagar — Transcranial Magnetic Stimulation",
    seoDescription:
      "rTMS (repetitive transcranial magnetic stimulation) in Bhavnagar at Manas Neuropsychiatry Hospital — FDA-approved, drug-free treatment for resistant depression, OCD, anxiety, PTSD and smoking cessation. TMS physician trained at AIIMS New Delhi. Sessions of 30 minutes, no anaesthesia. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/5e82a9f722c7e3ced8097f75e2d29d76a5215f8b737bbc90c1e97f4013c9f8e8.jpeg",
    imageAlt: "A patient seated in the rTMS chair at Manas Hospital with the figure-of-eight coil positioned over the scalp",
    caption: "A 30-minute session. You stay fully awake, then drive home or return to work.",
    intro:
      "Repetitive Transcranial Magnetic Stimulation uses a focused magnetic field — the same strength as an MRI pulse — to switch specific brain circuits back on. It is the most evidence-backed neuromodulation treatment available today, it involves no anaesthesia, no surgery and no systemic side-effects, and it is offered in our own suite in Bhavnagar by a TMS physician trained at AIIMS, New Delhi.",
    mechanism: [
      {
        title: "A magnetic pulse, not an electric shock",
        desc: "A figure-of-eight coil rests against the scalp and delivers rapid magnetic pulses. The magnetic field passes painlessly through skin and skull and induces a very small electrical current only in the targeted patch of cortex, about 2–3 cm deep.",
      },
      {
        title: "The under-active circuit is re-trained",
        desc: "In depression the left dorsolateral prefrontal cortex is typically under-active. High-frequency pulses delivered there over several weeks increase its excitability and restore its normal communication with the deeper mood-regulating circuits.",
      },
      {
        title: "The change outlasts the session",
        desc: "Repeated stimulation produces lasting neuroplastic change — which is why the benefit continues after the course ends, rather than wearing off in hours like a tablet.",
      },
    ],
    indicationGroups: [
      {
        label: "Strongest evidence",
        items: [
          "Treatment-resistant depression — two or more failed antidepressant trials",
          "Obsessive Compulsive Disorder, using FDA-cleared deep protocols",
          "Depression where medication side-effects are intolerable",
          "Depression in pregnancy or breastfeeding, where tablets are best avoided",
          "Smoking cessation — an approved short course to cut nicotine craving",
        ],
      },
      {
        label: "Also treated here",
        items: [
          "Generalised anxiety and panic disorder",
          "Post-Traumatic Stress Disorder",
          "Negative symptoms and persistent hallucinations in schizophrenia",
          "Alcohol and opioid craving as part of a deaddiction plan",
          "Migraine and treatment-refractory chronic pain",
          "Post-stroke cognitive and motor rehabilitation",
        ],
      },
    ],
    course: [
      { title: "Day 0 — Eligibility consultation", desc: "A full psychiatric review, a check of past medication trials, and a safety screen for metal implants, pacemakers and seizure history. We will tell you honestly if rTMS is not the right answer for you." },
      { title: "Day 1 — Motor threshold mapping", desc: "We find the exact intensity your brain responds to by watching for a small thumb twitch, then set your dose as a percentage of it. Your stimulation is personalised, not standard." },
      { title: "Weeks 1–4 — Acute course", desc: "Daily sessions of about 30 minutes, five days a week. You sit awake in a chair, you can talk, and most patients go straight back to work or college afterwards." },
      { title: "Weeks 5–6 — Taper and review", desc: "Sessions are spaced out as response is measured on the same rating scale used at baseline. We then agree a maintenance plan — usually occasional booster sessions." },
    ],
    sideEffects: [
      "Mild scalp tapping sensation or discomfort during the first few sessions",
      "Transient headache, which settles with a simple analgesic",
      "Brief facial or jaw muscle twitching while the coil is firing",
      "Temporary light-headedness immediately after standing up",
    ],
    notFor: [
      "Ferromagnetic metal implants, aneurysm clips or a cochlear implant in the head",
      "Cardiac pacemaker or implanted defibrillator",
      "Uncontrolled epilepsy or a very high seizure risk",
      "Active intracranial infection or a recent significant head injury",
    ],
    safetyNote:
      "rTMS has an excellent safety record — seizure risk is under 1 in 30,000 sessions. There is no memory loss, no anaesthesia and no sedation. Bring the full list of your current medicines and any old MRI or CT reports to the eligibility consultation.",
    faqs: [
      { q: "Is rTMS the same as electric shock treatment?", a: "No. They are entirely different. rTMS uses a magnetic field, you stay fully awake, there is no anaesthesia, no convulsion and no memory loss. ECT/mBPT induces a controlled seizure under anaesthesia and is used only for severe, life-threatening illness." },
      { q: "Is it painful?", a: "Most patients describe a tapping or knocking sensation on the scalp along with a clicking sound. It can feel odd for the first two or three sessions and then becomes routine. No sedation or painkiller is needed." },
      { q: "How many sessions will I need?", a: "A standard depression course is 20–30 daily sessions over 4–6 weeks. OCD and smoking-cessation protocols differ. You will be given the number, the schedule and the cost in writing before you start." },
      { q: "When will I notice a change?", a: "Sleep and energy usually shift first, around sessions 8–12. Mood typically lifts between sessions 15 and 25. Families often notice the improvement before the patient does." },
      { q: "Do I have to stop my medicines?", a: "No. rTMS is normally given alongside your existing medication, which often lets us keep doses lower. Any change to your prescription is a separate, deliberate decision." },
      { q: "Can I drive home and go to work?", a: "Yes. There is no sedation, so you can drive, work, study and do everything else immediately after a session. That is one of the main reasons patients choose it." },
      { q: "Does the benefit last?", a: "For most responders, yes — relapse-prevention is supported with continued medication and, where needed, booster sessions. Should symptoms return, a repeat course usually works again." },
      { q: "Is rTMS available in Bhavnagar itself?", a: "Yes. Our neuromodulation suite is at Kalanala, Bhavnagar, so patients from Sihor, Palitana, Talaja, Mahuva, Botad and Amreli do not need to travel to Ahmedabad or Mumbai for a daily course." },
    ],
    related: [
      { to: "/neuromodulation", label: "All neuromodulation therapies" },
      { to: "/conditions/depression", label: "Depression treatment" },
      { to: "/conditions/ocd", label: "OCD treatment" },
    ],
    procName: "Repetitive Transcranial Magnetic Stimulation (rTMS)",
    procType: "Noninvasive",
    treats: ["Treatment-resistant depression", "Obsessive compulsive disorder", "Anxiety disorder", "Post-traumatic stress disorder", "Nicotine dependence"],
  },

  mbpt: {
    slug: "mbpt",
    eyebrow: "mBPT · Modified Brief Pulse Therapy",
    h1a: "The treatment with the worst reputation",
    h1b: "and the fastest response.",
    seoTitle: "Modified Brief Pulse Therapy (mBPT) in Bhavnagar — Modern ECT",
    seoDescription:
      "Modified Brief Pulse Therapy (modern modified ECT) at Manas Neuropsychiatry Hospital, Bhavnagar — given under full anaesthesia with muscle relaxation and continuous monitoring for severe depression, catatonia, suicidal risk and resistant psychosis. What it is, how it is done, side-effects and consent explained. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/67d1f8736e9a6ced9a94f38886082330059d11df72281af8379d1137a06ca2d6.jpeg",
    imageAlt: "A monitored procedure room with anaesthesia machine and vital-signs monitor prepared for modified brief pulse therapy",
    caption: "Under anaesthesia, with an anaesthetist present and every vital sign monitored throughout.",
    intro:
      "Modified Brief Pulse Therapy is the modern form of electroconvulsive therapy: an ultra-brief, precisely measured electrical pulse given under full general anaesthesia with muscle relaxation, so there is no visible convulsion, no pain and no awareness of the procedure. Nothing in psychiatry works faster for severe, life-threatening illness — and no treatment is more misunderstood. We use it selectively, explain it completely, and never without informed written consent.",
    mechanism: [
      {
        title: "Brief pulse, not the old sine wave",
        desc: "Modern machines deliver a square brief-pulse current — a fraction of the electrical charge used by the old sine-wave machines shown in films. Less charge means the same clinical benefit with far less effect on memory.",
      },
      {
        title: "Anaesthesia and muscle relaxation",
        desc: "An anaesthetist gives a short-acting anaesthetic and a muscle relaxant before the pulse. You are asleep, your body stays still, and you remember nothing of the procedure. Oxygen, ECG, blood pressure and saturation are monitored continuously.",
      },
      {
        title: "A controlled reset of brain chemistry",
        desc: "The brief seizure triggers a rapid, widespread release and re-regulation of neurotransmitters and growth factors — producing improvement in days where medication takes weeks.",
      },
    ],
    indicationGroups: [
      {
        label: "When it is genuinely the right choice",
        items: [
          "Severe depression with active, high suicidal risk",
          "Catatonia — not eating, not speaking, not moving",
          "Refusal of food and fluids, with rapid physical deterioration",
          "Severe post-partum psychosis or depression needing a fast recovery",
          "Psychotic depression with delusions of guilt, poverty or illness",
          "Treatment-resistant schizophrenia and severe mania not responding to medication",
        ],
      },
      {
        label: "How we decide",
        items: [
          "Two consultant psychiatrists review the indication independently",
          "Physical fitness and anaesthesia clearance are confirmed first",
          "Risks, benefits and alternatives are explained in your own language",
          "Written informed consent is taken from the patient wherever capacity allows",
          "The minimum effective number of sessions is planned, not a fixed 'package'",
          "Medication and therapy continue alongside, never as a replacement",
        ],
      },
    ],
    course: [
      { title: "Pre-procedure work-up", desc: "ECG, blood investigations, physician and anaesthesia assessment, and a review of every current medicine. Nothing to eat or drink for six hours before each session." },
      { title: "The session itself", desc: "Roughly 10–15 minutes in the procedure room: anaesthesia, muscle relaxant, oxygen, the pulse, then recovery. The stimulus itself lasts under eight seconds." },
      { title: "Recovery and observation", desc: "You wake within a few minutes and are observed until fully alert and oriented, usually 30–60 minutes. Mild confusion and headache on waking are common and short-lived." },
      { title: "Course and after", desc: "Typically 6–10 sessions, two or three per week, with response measured after every second session. Medication is then optimised to hold the gain, occasionally with maintenance sessions." },
    ],
    sideEffects: [
      "Headache, jaw ache or muscle soreness for a few hours after a session",
      "Short-lived confusion on waking, clearing within 30–60 minutes",
      "Difficulty recalling events around the period of treatment, which usually recovers over weeks",
      "Nausea related to the anaesthetic",
      "Small, anaesthesia-related risks, which is why fitness is assessed before every course",
    ],
    notFor: [
      "Recent heart attack, unstable cardiac disease or uncontrolled heart failure",
      "Raised intracranial pressure or a space-occupying brain lesion",
      "Recent stroke or an unstable cerebral aneurysm",
      "Unfit for general anaesthesia on physician assessment",
      "Mild or moderate illness that can be treated safely with medication and therapy",
    ],
    safetyNote:
      "mBPT is not a punishment, not a 'shock' in the way films depict it, and never given without consent. If a family member has been advised this treatment and you are frightened by what you have heard, come and ask us directly — we will show you the room, the machine and the consent form before you decide.",
    faqs: [
      { q: "Is this the same as the electric shock treatment in films?", a: "No. Those scenes show 1950s sine-wave machines used without anaesthesia. mBPT uses an ultra-brief pulse with a fraction of the charge, given under full anaesthesia and muscle relaxation — you are asleep, your body does not convulse visibly, and you feel nothing." },
      { q: "Will it damage my brain or erase my memory?", a: "No structural brain damage occurs — this has been studied extensively with imaging. Some patients do have patchy difficulty recalling events from around the treatment period; this typically improves over the following weeks. Long-term new-learning ability is not impaired." },
      { q: "Is it painful?", a: "No. You are under general anaesthesia for the procedure. Afterwards some patients have a headache or jaw ache for a few hours, which settles with a simple painkiller." },
      { q: "Why choose this instead of tablets?", a: "Speed. When a patient is actively suicidal, catatonic or refusing food, waiting three to four weeks for a tablet to act is itself dangerous. mBPT often produces visible improvement within the first two or three sessions." },
      { q: "How many sessions are needed?", a: "Usually six to ten, given two or three times a week. We reassess after every second session and stop as soon as the response is adequate — there is no fixed package." },
      { q: "Can it be given without the patient's consent?", a: "Only in the specific emergency circumstances permitted by the Mental Healthcare Act, 2017, with the safeguards that law requires. In routine practice we take written informed consent from the patient, and from the nominated representative where capacity is impaired." },
      { q: "Is admission necessary?", a: "Most courses are done with admission for at least the initial sessions, because these are severely ill patients. Later maintenance sessions can sometimes be done as a day-care procedure." },
      { q: "Will the illness come back after the course?", a: "mBPT treats the current episode rapidly; medication, therapy and follow-up prevent the next one. Stopping treatment entirely after a good mBPT response is the commonest reason for relapse." },
    ],
    related: [
      { to: "/neuromodulation", label: "All neuromodulation therapies" },
      { to: "/conditions/suicide-crisis", label: "Suicide risk & crisis care" },
      { to: "/conditions/schizophrenia", label: "Schizophrenia & psychosis" },
    ],
    procName: "Modified Brief Pulse Therapy (modified electroconvulsive therapy)",
    procType: "PercutaneousProcedure",
    treats: ["Severe major depressive disorder", "Catatonia", "Psychotic depression", "Treatment-resistant schizophrenia", "Severe mania"],
  },

  tdcs: {
    slug: "tdcs",
    eyebrow: "tDCS · Low-intensity · Gentle",
    h1a: "The gentlest tool",
    h1b: "in the suite.",
    seoTitle: "tDCS Treatment in Bhavnagar — Transcranial Direct Current Stimulation",
    seoDescription:
      "Transcranial Direct Current Stimulation (tDCS) at Manas Neuropsychiatry Hospital, Bhavnagar — a painless, low-intensity, drug-free brain stimulation used for depression, craving, cognitive rehabilitation after stroke or head injury, and chronic pain. Sessions of 20–30 minutes, no anaesthesia. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/031a3af9e5c168dd6a20a1a0ad33a770e84ce11745723cf03a62f75c7d48cae6.jpeg",
    imageAlt: "A patient wearing a tDCS headband with sponge electrodes while a doctor sets the stimulator current",
    caption: "Two sponge electrodes, a current you can barely feel, and a 20-minute session you can read through.",
    intro:
      "Transcranial Direct Current Stimulation passes a very weak constant current — typically 1 to 2 milliamperes, a small fraction of what rTMS delivers — between two sponge electrodes on the scalp. It does not fire neurons; it gently nudges them towards being easier or harder to fire. That makes it painless, extremely well tolerated, and particularly useful in combination with therapy, cognitive training and craving management.",
    mechanism: [
      {
        title: "It shifts excitability, it does not force firing",
        desc: "The anode makes the cortex underneath slightly more likely to fire; the cathode makes it slightly less likely. Nothing is forced — which is why the current is imperceptible beyond a mild tingle.",
      },
      {
        title: "It works best while the brain is busy",
        desc: "Because tDCS only biases activity, we pair it with an active task — cognitive exercises, speech or hand rehabilitation, or craving-exposure work — so the circuit being stimulated is the circuit being used.",
      },
      {
        title: "Repetition builds the change",
        desc: "A single session lasts hours; a course of daily sessions produces cumulative plasticity. This is why tDCS is prescribed as a block of sessions rather than a one-off.",
      },
    ],
    indicationGroups: [
      {
        label: "Commonly used for",
        items: [
          "Mild to moderate depression, especially when medication is not tolerated",
          "Alcohol, tobacco and opioid craving during deaddiction treatment",
          "Cognitive rehabilitation after stroke or traumatic brain injury",
          "Persistent auditory hallucinations in schizophrenia",
          "Chronic pain, fibromyalgia and refractory migraine",
        ],
      },
      {
        label: "Why patients are offered it",
        items: [
          "No anaesthesia, no sedation, no systemic side-effects",
          "Substantially lower cost per session than rTMS",
          "Safe to combine with existing medication and therapy",
          "Suitable when a pacemaker rules out other options, after assessment",
          "Sessions can be scheduled twice a day to shorten a course",
          "Usable in older patients and in adolescents where appropriate",
        ],
      },
    ],
    course: [
      { title: "Assessment and target selection", desc: "A psychiatric and, where relevant, cognitive assessment decides which cortical target and which electrode montage suits your problem. Scalp skin and any implants are checked." },
      { title: "The session", desc: "Saline-soaked sponge electrodes are held in place by an elastic band, current is ramped up over 30 seconds, and 20–30 minutes pass while you sit, read or do the paired task." },
      { title: "Course of 10–20 sessions", desc: "Daily or twice-daily sessions on weekdays, with symptom or cognitive scores repeated at the midpoint so progress is measured rather than assumed." },
      { title: "Review and maintenance", desc: "At the end of the block we decide honestly whether to continue, switch to rTMS, or rely on medication and therapy alone." },
    ],
    sideEffects: [
      "Tingling, itching or a mild warm sensation under the electrodes",
      "Transient redness of the scalp skin where the sponge rested",
      "Mild headache or fatigue after a session, in a minority of patients",
      "Brief phosphene — a flicker of light — as the current is switched on",
    ],
    notFor: [
      "Metal implants, plates or clips in the head",
      "Skin disease, open wounds or infection at the electrode site",
      "Implanted pacemaker or defibrillator, unless specifically cleared",
      "Poorly controlled epilepsy",
    ],
    safetyNote:
      "tDCS is safe but not magic: consumer 'brain-boosting' headsets sold online are unregulated and use uncontrolled montages. Stimulation should be prescribed and supervised, with the target, current and duration chosen for your diagnosis.",
    faqs: [
      { q: "How is tDCS different from rTMS?", a: "rTMS uses strong magnetic pulses that directly make neurons fire, and has the larger evidence base for resistant depression and OCD. tDCS uses a very weak constant current that only makes firing more or less likely — gentler, cheaper, and best used alongside therapy or rehabilitation tasks." },
      { q: "Will I feel the current?", a: "Only a mild tingle or itch under the electrodes for the first minute, after which most patients stop noticing it entirely. Nothing about the session is painful." },
      { q: "Can I use it instead of my antidepressant?", a: "In mild depression it can be a genuine drug-sparing option. In moderate to severe depression it is an add-on, not a replacement — and we will say so plainly rather than let you stop effective medication." },
      { q: "How many sessions, and how long does each take?", a: "Usually 10 to 20 weekday sessions of 20–30 minutes. Because there is no sedation, patients often come before work or after college." },
      { q: "Does it help with alcohol or tobacco craving?", a: "Yes — stimulating the dorsolateral prefrontal cortex reduces craving intensity in many patients, and we use it as part of a wider deaddiction plan rather than on its own." },
      { q: "Can I buy a home device?", a: "We do not recommend it. The devices sold online are unregulated, the montages are guesswork, and electrode burns from poor technique are a real risk. Supervised sessions cost little more and are actually targeted." },
      { q: "Is it safe for elderly patients?", a: "Yes, and it is frequently chosen for older patients precisely because there is no anaesthesia and no interaction with cardiac or diabetic medication." },
    ],
    related: [
      { to: "/neuromodulation", label: "All neuromodulation therapies" },
      { to: "/therapies/rtms", label: "rTMS — the stronger option" },
      { to: "/deaddiction", label: "Deaddiction programme" },
    ],
    procName: "Transcranial Direct Current Stimulation (tDCS)",
    procType: "Noninvasive",
    treats: ["Major depressive disorder", "Substance craving", "Post-stroke cognitive impairment", "Auditory hallucinations", "Chronic pain"],
  },

  "ketamine-infusion": {
    slug: "ketamine-infusion",
    eyebrow: "Ketamine IV · Rapid-acting · Monitored",
    h1a: "When there is no time",
    h1b: "to wait four weeks.",
    seoTitle: "Ketamine IV Infusion Therapy in Bhavnagar — Rapid-Acting Depression Treatment",
    seoDescription:
      "Ketamine IV infusion therapy at Manas Neuropsychiatry Hospital, Bhavnagar — sub-anaesthetic, continuously monitored infusions for treatment-resistant depression and acute suicidal ideation, with relief often within hours. Protocol, safety, monitoring and cost explained. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/e3c94338fd218ab6b6b76982d20933ece7ec0da79babbdf821fb64fb6308a778.jpeg",
    imageAlt: "A patient resting in an infusion chair with an IV line and vital-signs monitor during ketamine infusion therapy",
    caption: "Forty minutes in a quiet room, with blood pressure, pulse and oxygen watched throughout.",
    intro:
      "Given slowly at a sub-anaesthetic dose under continuous medical monitoring, intravenous ketamine can lift severe depression and, crucially, suicidal thinking within hours rather than weeks. It is not a recreational experience and not a first-line antidepressant: it is a rapid-acting rescue and bridging treatment for patients who have not responded to standard medication, or who are too unwell to wait for one to work.",
    mechanism: [
      {
        title: "A different receptor altogether",
        desc: "Standard antidepressants act on serotonin and noradrenaline. Ketamine blocks the NMDA glutamate receptor, setting off a surge of synaptic growth signalling that conventional drugs reach only indirectly and far more slowly.",
      },
      {
        title: "Synapses regrow within hours",
        desc: "Chronic stress and depression thin out prefrontal synaptic connections. Ketamine rapidly promotes their regrowth, which is why mood and hopelessness can shift the same day.",
      },
      {
        title: "Specific action on suicidal thinking",
        desc: "The anti-suicidal effect appears earlier and somewhat independently of the antidepressant effect — making it valuable exactly when risk is highest and time is shortest.",
      },
    ],
    indicationGroups: [
      {
        label: "Who we offer it to",
        items: [
          "Treatment-resistant depression after two or more adequate medication trials",
          "Acute, active suicidal ideation needing a response in hours, not weeks",
          "Severe depression where mBPT is refused or medically unsuitable",
          "Bipolar depression not responding to mood stabilisers, with cover in place",
          "Severe PTSD and refractory obsessive symptoms, in selected cases",
        ],
      },
      {
        label: "How we keep it safe",
        items: [
          "Sub-anaesthetic dose calculated by body weight, infused slowly over ~40 minutes",
          "Continuous blood pressure, pulse, ECG and oxygen saturation monitoring",
          "A doctor present in the room for the entire infusion",
          "A quiet, dimly lit setting with a trained nurse and an escort home",
          "Nothing to eat for four hours beforehand; no driving for 24 hours",
          "Antidepressants, therapy and follow-up continue alongside — never instead",
        ],
      },
    ],
    course: [
      { title: "Screening", desc: "Psychiatric assessment, blood pressure and cardiac review, urine screen where relevant, and a clear explanation of the dissociative sensations you may experience. Written consent is taken." },
      { title: "The infusion", desc: "An IV cannula, then a slow 40-minute infusion. Many patients feel pleasantly detached, floaty or notice sounds and colours differently; this fades within 20–30 minutes of the infusion ending." },
      { title: "Induction series", desc: "Typically six infusions over two to three weeks, with mood and suicidality re-rated before each one. Non-responders after the first three are told honestly, and we change plan." },
      { title: "Maintenance and handover", desc: "Responders are transitioned onto oral medication, therapy and relapse-prevention work, with spaced booster infusions only where clearly needed." },
    ],
    sideEffects: [
      "Dissociation — feeling floaty, detached or dreamlike during the infusion",
      "Rise in blood pressure and pulse while the drug is running",
      "Nausea, and occasionally vomiting, managed with pre-medication",
      "Blurred vision, dizziness and unsteadiness for an hour afterwards",
      "Headache and tiredness for the rest of the day",
    ],
    notFor: [
      "Uncontrolled hypertension, unstable angina or recent myocardial infarction",
      "Active psychosis or mania",
      "Raised intracranial pressure or untreated glaucoma",
      "Ongoing ketamine or other substance misuse",
      "Pregnancy, and significant uncorrected liver disease",
    ],
    safetyNote:
      "Ketamine is a controlled drug with genuine misuse potential. It should only ever be given in a monitored clinical setting with a defined protocol and a written exit plan — never by prescription for home use, and never as an open-ended series of infusions.",
    faqs: [
      { q: "How quickly does it work?", a: "Many patients report a clear lift in mood and a drop in hopelessness within 2 to 24 hours of the first infusion. That speed is the entire reason for using it in a crisis." },
      { q: "Is it addictive?", a: "Used at sub-anaesthetic doses, in a clinic, on a fixed protocol with a defined endpoint, the risk is low. Misuse risk comes from unsupervised or repeated non-clinical use, which is exactly why we do not prescribe it for home use." },
      { q: "What does the infusion feel like?", a: "Most patients feel pleasantly detached, floaty or dreamlike, with time and sound seeming different. It is not frightening for the great majority — the room is quiet, someone stays with you, and it wears off within half an hour of the drip ending." },
      { q: "Will I need admission?", a: "Usually no. Infusions are done as a day-care procedure, but you must have an adult escort home, must not drive for 24 hours, and should not be alone that night." },
      { q: "How long does the benefit last?", a: "A single infusion often lasts days to a couple of weeks. That window is used to get conventional treatment, therapy and safety planning working — ketamine is the bridge, not the destination." },
      { q: "Can I have it instead of mBPT?", a: "In many patients, yes — and some choose it for exactly that reason. In catatonia, food refusal or psychotic depression, mBPT remains faster and more reliable, and we will tell you which applies." },
      { q: "Is the nasal spray the same thing?", a: "Esketamine nasal spray is a related, licensed product used with similar monitoring. IV infusion allows precise weight-based dosing and is what we offer here." },
    ],
    related: [
      { to: "/neuromodulation", label: "All neuromodulation therapies" },
      { to: "/conditions/suicide-crisis", label: "Suicide risk & crisis care" },
      { to: "/conditions/depression", label: "Depression treatment" },
    ],
    procName: "Ketamine Intravenous Infusion Therapy",
    procType: "PercutaneousProcedure",
    treats: ["Treatment-resistant depression", "Suicidal ideation", "Bipolar depression", "Post-traumatic stress disorder"],
  },

  "naltrexone-implant": {
    slug: "naltrexone-implant",
    eyebrow: "Naltrexone Implant · Deaddiction",
    h1a: "One implant.",
    h1b: "Months without the daily fight.",
    seoTitle: "Naltrexone Implant in Bhavnagar — Long-Acting Deaddiction Treatment",
    seoDescription:
      "Naltrexone drug implant for alcohol and opioid dependence at Manas Neuropsychiatry Hospital, Bhavnagar — a slow-release implant placed under the skin that blocks craving for months, removing the daily tablet burden and protecting the vulnerable post-detox phase. Procedure, safety and warnings explained. Call +91 94275 60101.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/29f32df3ea13a822cc98eba4260bdf3cca6f13412b8941478f7d3d2b8461c51f.jpeg",
    imageAlt: "A sterile tray prepared for a minor naltrexone implant procedure in a hospital procedure room",
    caption: "A 20-minute minor procedure under local anaesthesia, then months of steady protection.",
    intro:
      "Naltrexone blocks the opioid receptors that alcohol and opioids act on, so using them no longer produces the expected effect — and craving fades. The problem with naltrexone tablets has never been efficacy; it is that a patient in craving simply stops taking them. A slow-release implant placed under the skin removes that daily decision altogether and protects the first, most dangerous months after detoxification.",
    mechanism: [
      {
        title: "The receptor is occupied, so the reward is absent",
        desc: "Naltrexone is an opioid antagonist. With the receptor blocked, a drink or a dose does not deliver its usual reward, and the learned loop of craving and relief begins to break.",
      },
      {
        title: "Slow, steady release for months",
        desc: "The implant sits in the fat layer of the lower abdominal wall and releases naltrexone continuously, holding a therapeutic level for several months — no peaks, no missed doses, no daily negotiation with yourself.",
      },
      {
        title: "Adherence is the real mechanism",
        desc: "Relapse in the first six months is overwhelmingly a story of stopped medication. An implant cannot be skipped in a bad moment, which is why it changes outcomes so noticeably.",
      },
    ],
    indicationGroups: [
      {
        label: "Who benefits most",
        items: [
          "Alcohol dependence with repeated relapse after detoxification",
          "Opioid dependence — heroin, tramadol, injectable or prescription opioids",
          "Patients who have repeatedly stopped their oral naltrexone",
          "High-risk post-detox phase, especially the first three to six months",
          "Patients living away from family supervision or working outstation",
        ],
      },
      {
        label: "Non-negotiable prerequisites",
        items: [
          "Fully detoxified and opioid-free — 7 to 10 days for short-acting opioids, longer for others",
          "A negative urine screen and, where needed, a naloxone challenge before insertion",
          "Normal liver function on blood testing",
          "The patient's own informed consent — this is never done covertly on family request",
          "Agreement to continue counselling and follow-up, not the implant alone",
          "A written medical alert card in case of emergency surgery or injury",
        ],
      },
    ],
    course: [
      { title: "Detox first", desc: "Supervised withdrawal management in OPD or as inpatient. Inserting an implant in someone who is not fully opioid-free precipitates severe withdrawal, so this step is never rushed." },
      { title: "The insertion", desc: "A 20-minute minor sterile procedure under local anaesthesia in the lower abdominal wall, through a small incision closed with absorbable sutures. You go home the same day." },
      { title: "First fortnight", desc: "Wound review, simple dressing care, no swimming or heavy lifting, and a watch for local redness or swelling. Counselling begins immediately in parallel." },
      { title: "Months 1–6 and repeat", desc: "Monthly review with craving and liver monitoring. Where risk remains high, the implant is repeated — and where it does not, a transition to oral medication and therapy alone is planned." },
    ],
    sideEffects: [
      "Local pain, bruising or swelling at the implant site for a few days",
      "Occasional local infection or delayed wound healing, needing review",
      "Nausea, headache or disturbed sleep in the first week",
      "Extrusion or migration of the implant, uncommon and easily managed",
      "A rise in liver enzymes, which is why liver function is checked before and during",
    ],
    notFor: [
      "Anyone not fully detoxified from opioids — this precipitates acute withdrawal",
      "Significant liver failure or acute hepatitis",
      "Need for ongoing opioid analgesia, such as cancer pain",
      "Pregnancy or breastfeeding",
      "Known naltrexone hypersensitivity, or active skin infection at the site",
    ],
    safetyNote:
      "Important safety warning: while an implant is in place, opioid painkillers will not work in an accident or emergency surgery — always carry your medical alert card and inform any treating doctor. Equally, attempting to override the block with a large dose of opioids is extremely dangerous, and tolerance falls sharply once the implant wears off, which is when overdose risk peaks. This is why follow-up matters as much as the implant.",
    faqs: [
      { q: "How long does one implant last?", a: "Depending on the preparation and dose used, a therapeutic level is generally maintained for around three to six months. You will be told the expected duration for your implant and the date to return." },
      { q: "Will it stop the craving completely?", a: "It substantially blunts craving and removes the reward from drinking or using, which for most patients is the difference between a slip and a full relapse. It does not remove the psychological and social triggers — counselling does that work." },
      { q: "Can it be given without telling the patient?", a: "No. We do not insert implants covertly at a family's request, however desperate the situation. Treatment begins with the patient's own informed consent." },
      { q: "Is the procedure painful?", a: "It is done under local anaesthesia, so you feel pressure rather than pain. Mild soreness at the site for two or three days is normal and settles with a simple painkiller." },
      { q: "What happens if I drink alcohol anyway?", a: "You will not get the expected pleasurable effect, which is the point. Drinking heavily on naltrexone is still harmful to the liver and is not a test worth running." },
      { q: "What if I need surgery or have an accident?", a: "Tell the treating doctor immediately and show your medical alert card — standard opioid painkillers will be ineffective and alternative anaesthetic and analgesic plans are needed." },
      { q: "Is it a cure for addiction?", a: "No. It is the most reliable protective scaffolding available for the phase when relapse is most likely. Recovery still comes from counselling, family work, routine and follow-up — the implant simply buys you the months in which to build them." },
      { q: "Can it be removed?", a: "Yes. If there is a medical need — a severe local reaction, or a requirement for opioid analgesia — the implant can be surgically removed." },
    ],
    related: [
      { to: "/deaddiction", label: "Deaddiction programme" },
      { to: "/neuromodulation", label: "All neuromodulation therapies" },
      { to: "/gu/deaddiction", label: "વ્યસનમુક્તિ (ગુજરાતીમાં)" },
    ],
    procName: "Naltrexone Drug Implant",
    procType: "SurgicalProcedure",
    treats: ["Alcohol use disorder", "Opioid dependence", "Relapse prevention after detoxification"],
  },
};

export const therapyJsonLd = (th) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalWebPage",
      "@id": `${SITE}/therapies/${th.slug}`,
      name: th.seoTitle,
      description: th.seoDescription,
      inLanguage: "en-IN",
      about: {
        "@type": "MedicalProcedure",
        name: th.procName,
        procedureType: th.procType,
        howPerformed: th.course.map((s) => `${s.title}: ${s.desc}`).join(" "),
        preparation: th.course[0]?.desc,
        followup: th.course[th.course.length - 1]?.desc,
        seriousAdverseOutcome: { "@type": "MedicalEntity", name: th.notFor[0] },
        indication: th.treats.map((t) => ({ "@type": "MedicalIndication", name: t })),
      },
      publisher: clinicNode,
    },
    {
      "@type": "FAQPage",
      mainEntity: th.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
});
